// useStsGenerate — the explicit [Generate] step of the Recording Booth (split
// out of RecordingStudio.tsx). Sends the loaded source through the STS API
// and files the result into the voice-keyed take slot.

import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { flushSync } from 'react-dom';
import type { STSParams } from '@shared/types';
import { RECORDING_SESSION_TAKE_LIMIT } from '@shared/constants';
import { generateSts } from '@/lib/api';
import { describeGenerationError } from '@/lib/generationErrors';
import { decodeAndCache } from '@/lib/audioPlayback';
import { planDraftSlot } from './draftSlot';
import type { DraftTake, LoadedSource, RecState } from './boothTypes';

interface UseStsGenerateArgs {
  toolId: string | null;
  loadedSource: LoadedSource | null;
  /** Picker-selected target voice (multi-voice) — falls back to the asset's primary voice. */
  currentVoiceId: string;
  activeAssetVoiceId: string | undefined;
  sessionNum: number | null;
  params: STSParams;
  /** Snapshot of the current takes (see RecordingStudio: keeps `takes` out of deps). */
  takesRef: MutableRefObject<DraftTake[]>;
  setTakes: Dispatch<SetStateAction<DraftTake[]>>;
  setState: Dispatch<SetStateAction<RecState>>;
  setPlayingTakeId: Dispatch<SetStateAction<string | null>>;
  toast: { show(message: string, ms?: number): void };
}

// Phase 6 (#173 Task 6.3): explicit Generate with voice-keyed take slots.
//
// Slot key = (session, voiceId). Within ONE recording session:
//   - Same voice + Generate again → existing take is REPLACED in-place
//     (preserving its `takeInSession` label so the user still sees
//     "Take 3-1" rather than seeing it renumbered to a 6th take).
//   - Different voice + Generate → new slot appended with the next
//     unused `takeInSession` number for this session.
//
// The take-list cap (`RECORDING_SESSION_TAKE_LIMIT`) only bites when the
// user is creating a NEW slot — replacements never push the count up.
export function useStsGenerate({
  toolId,
  loadedSource,
  currentVoiceId,
  activeAssetVoiceId,
  sessionNum,
  params,
  takesRef,
  setTakes,
  setState,
  setPlayingTakeId,
  toast,
}: UseStsGenerateArgs): () => Promise<void> {
  return useCallback(async () => {
    if (!loadedSource || !toolId) return;
    const targetVoiceId = currentVoiceId || activeAssetVoiceId;
    if (!targetVoiceId) return;
    const session = sessionNum ?? 1;
    // Slot lookup: (session, voiceId) — keyed match against the LOCAL
    // overlay take list. Marked takes lock the slot (re-generate path is
    // already covered by `regenerateTake` and explicitly disallowed when
    // marked); for unmarked slots, this is a clean replace.
    const existing = takesRef.current.find(
      (t) => t.session === session && t.voiceId === targetVoiceId,
    );
    if (existing?.marked) {
      // The slot is locked. Refuse the generate so the user can't lose
      // a take they've already committed to keeping.
      toast.show(
        'This voice already has a marked take in this session — unmark it to re-generate.',
        3500,
      );
      return;
    }
    // Limit-check ONLY when this would be a NEW slot. Replacement bypasses
    // it (the count isn't growing).
    if (!existing && takesRef.current.length >= RECORDING_SESSION_TAKE_LIMIT) {
      toast.show(
        `Session take limit (${RECORDING_SESSION_TAKE_LIMIT}) reached — delete a take to generate again.`,
        3500,
      );
      return;
    }
    setState({ kind: 'generating' });
    try {
      const audioBuffer = await generateSts({
        toolId,
        audio: loadedSource.blob,
        audioMime: loadedSource.mime,
        voiceId: targetVoiceId,
        params,
      });
      const inputBuffer = await loadedSource.blob.arrayBuffer();
      const outBlob = new Blob([audioBuffer], { type: 'audio/mpeg' });
      const outputUrl = URL.createObjectURL(outBlob);
      const inputUrl = URL.createObjectURL(loadedSource.blob);
      // Frische ID auch beim ReGenerate (Audit M24): Mit der alten ID lieferte
      // der Decode-Cache (decodeAndCache, nach Take-ID) den alten Puffer —
      // falsche Dauer, falscher outPoint, altes Audio auf der Karte. Label
      // und Markierung bleiben erhalten (planDraftSlot).
      const slot = planDraftSlot(takesRef.current, session, targetVoiceId);
      const id = slot.id;
      const duration = await probeAudioDuration(audioBuffer, id);
      const takeInSession = slot.takeInSession;
      const draft: DraftTake = {
        id,
        audioBuffer,
        inputBuffer,
        inputMime: loadedSource.mime,
        duration,
        createdAt: new Date().toISOString(),
        params: { ...params },
        voiceId: targetVoiceId,
        session,
        takeInSession,
        marked: slot.marked,
        outputUrl,
        inputUrl,
      };
      setTakes((prev) => {
        const idx = prev.findIndex(
          (t) => t.session === session && t.voiceId === targetVoiceId,
        );
        if (idx >= 0) {
          // Revoke the old output URL — the input URL is the same source
          // we're re-using, so keep that one. Actually the input is also
          // freshly URL'd above; revoke the old input too.
          const old = prev[idx];
          try { URL.revokeObjectURL(old.outputUrl); } catch { /* ignore */ }
          try { URL.revokeObjectURL(old.inputUrl); } catch { /* ignore */ }
          const next = prev.slice();
          next[idx] = draft;
          return next;
        }
        return [...prev, draft];
      });
      setState({ kind: 'idle' });
      // Auto-play the just-generated take. flushSync the pause to commit
      // before the play-id swap so the pause-effect fires even when the
      // same slot was already playing (overwrite-while-playing case) —
      // then the auto-play effect picks up the fresh draft.id.
      flushSync(() => setPlayingTakeId(null));
      setPlayingTakeId(draft.id);
    } catch (e: unknown) {
      // Talent-Sprache statt Roh-JSON aus dem Dienst (lib/generationErrors, Audit M13).
      const msg = describeGenerationError(e);
      setState({ kind: 'error', message: msg });
      toast.show(msg, 5000);
    }
  }, [activeAssetVoiceId, currentVoiceId, loadedSource, params, sessionNum, toast, toolId, takesRef, setTakes, setState, setPlayingTakeId]);
}

/** Probe an audio ArrayBuffer for its duration via Web Audio decode. We
 *  decode through the unified `decodeAndCache` so the resulting AudioBuffer
 *  is already cached for the imminent waveform / take-card playback — no
 *  double work. */
async function probeAudioDuration(buffer: ArrayBuffer, cacheKey: string): Promise<number> {
  try {
    const decoded = await decodeAndCache(buffer.slice(0), cacheKey);
    return Number.isFinite(decoded.duration) && decoded.duration > 0
      ? decoded.duration
      : 0;
  } catch {
    return 0;
  }
}
